import React, {
  useState,
  useEffect,
  useCallback,
  useMemo,
  useRef,
} from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import ReactFlow, {
  Node,
  Edge,
  Background,
  Controls,
  useNodesState,
  useEdgesState,
  NodeTypes,
  Connection,
  addEdge,
  Handle,
  Position,
} from "reactflow";
import "reactflow/dist/style.css";
import { usePermissions } from "../../PermissionsProvider";
import { AgentService } from "../../services/AgentService";
import { Agent } from "../../model_gen/agent_base";
import { BarChart3Icon } from "lucide-react";
import AddCapabilityModal from "./AddCapabilityModal";

// Layout constants
const NODE_WIDTH = 350;
const MIN_SEPARATION = 80;
const DISTANT_SUBTREE_SEPARATION = 200;
const DEFAULT_NODE_HEIGHT = 80;
const LEVEL_VERTICAL_SPACING = 150;

// Tree node interface for layout calculations
interface TreeNode {
  id: string;
  width: number;
  height: number;
  children: TreeNode[];
  x: number;
  y: number;
  level: number;
  parent?: string;
  hasManualPosition: boolean;
}

// Custom node component
const CapabilityNode = ({ data }: { data: any }) => {
  const getNodeTypeClasses = (nodeType: string, isDisabled: boolean) => {
    const typeClasses: Record<string, string> = {
      agent:
        "border-[#21b892] text-[#21b892] dark:text-white bg-card [background-image:linear-gradient(rgba(33,184,146,0.1),rgba(33,184,146,0.1))] dark:[background-image:linear-gradient(rgba(33,184,146,0.2),rgba(33,184,146,0.2))] font-semibold text-base px-5 py-4",
      "action-api":
        "border-[#ffdb6a] text-yellow-700 dark:text-white bg-card [background-image:linear-gradient(rgba(255,219,106,0.1),rgba(255,219,106,0.1))] dark:[background-image:linear-gradient(rgba(255,219,106,0.2),rgba(255,219,106,0.2))] cursor-pointer",
      "action-llm":
        "border-[#ffdb6a] text-yellow-700 dark:text-white bg-card [background-image:linear-gradient(rgba(255,219,106,0.1),rgba(255,219,106,0.1))] dark:[background-image:linear-gradient(rgba(255,219,106,0.2),rgba(255,219,106,0.2))] cursor-pointer",
      "action-webhook":
        "border-[#9b87f5] text-purple-700 dark:text-white bg-card [background-image:linear-gradient(rgba(155,135,245,0.1),rgba(155,135,245,0.1))] dark:[background-image:linear-gradient(rgba(155,135,245,0.2),rgba(155,135,245,0.2))] cursor-pointer",
      "action-workflow":
        "border-[#0ea5e9] text-sky-700 dark:text-white bg-card [background-image:linear-gradient(rgba(14,165,233,0.1),rgba(14,165,233,0.1))] dark:[background-image:linear-gradient(rgba(14,165,233,0.2),rgba(14,165,233,0.2))] cursor-pointer",
      subagent:
        "border-[#ea5f40] text-[#ea5f40] dark:text-white bg-card [background-image:linear-gradient(rgba(234,95,64,0.1),rgba(234,95,64,0.1))] dark:[background-image:linear-gradient(rgba(234,95,64,0.2),rgba(234,95,64,0.2))] cursor-pointer",
      disabled:
        "border-gray-300/60 dark:border-gray-600/60 text-gray-500/60 dark:text-gray-400/60 bg-card [background-image:linear-gradient(rgba(156,163,175,0.1),rgba(156,163,175,0.1))] dark:[background-image:linear-gradient(rgba(156,163,175,0.2),rgba(156,163,175,0.2))]",
    };

    if (isDisabled) {
      return typeClasses["disabled"];
    }

    return typeClasses[nodeType] || "";
  };

  const getClassName = () => {
    const baseClasses =
      "px-3 py-2 rounded-lg border-2 text-center hover:shadow-md transition-all duration-200 w-[350px] break-words relative";
    const nodeTypeClasses = getNodeTypeClasses(data.nodeType, data.isDisabled);
    return `${baseClasses} ${nodeTypeClasses}`;
  };

  const handleMainClick = (e: React.MouseEvent) => {
    // If clicking the expansion button, don't trigger the main onClick
    if ((e.target as HTMLElement).closest(".expansion-button")) {
      return;
    }
    data.onClick?.();
  };

  const handleExpansionClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    data.toggleExpansion?.();
  };

  const hasChildren = data.childCount > 0;
  const showExpansionButton = hasChildren && data.nodeType === "subagent"; // Only show for sub-agents, not the root agent

  return (
    <div className="relative">
      <div className={getClassName()} onClick={handleMainClick}>
        <Handle
          type="target"
          position={Position.Top}
          id="target"
          style={{ background: "#555" }}
        />

        <div className="font-medium text-sm leading-tight break-words hyphens-auto">
          {data.label}
        </div>
        {data.description && (
          <div className="text-xs mt-1 opacity-75 leading-tight break-words hyphens-auto line-clamp-3">
            {data.description}
          </div>
        )}

        <Handle
          type="source"
          position={Position.Bottom}
          id="source"
          style={{ background: "#555" }}
        />
      </div>

      {/* Child count and expansion button positioned below the node */}
      {showExpansionButton && (
        <div className="absolute top-full left-1/2 transform -translate-x-1/2 mt-2 z-10">
          <button
            className="expansion-button px-3 py-1 bg-card border border-border rounded text-xs font-gc-reg text-muted-foreground hover:text-foreground transition-colors duration-200"
            onClick={handleExpansionClick}>
            {data.isExpanded ? "−" : "+"} {data.childCount}{" "}
            {data.childCount === 1 ? "child" : "children"}
          </button>
        </div>
      )}
    </div>
  );
};

interface AgentCapabilitiesGraphProps {
  agentId: number;
  agentName?: string;
  onAddCapability?: () => void;
  closeAllChildrenRef?: React.MutableRefObject<(() => void) | null>;
  onExpandedStateChange?: (hasExpandedChildren: boolean) => void;
}

const nodeTypes: NodeTypes = {
  capability: CapabilityNode,
};

const AgentCapabilitiesGraph: React.FC<AgentCapabilitiesGraphProps> = ({
  agentId,
  agentName = "Agent",
  onAddCapability,
  closeAllChildrenRef,
  onExpandedStateChange,
}) => {
  const { apiKey } = usePermissions();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [fullAgentTree, setFullAgentTree] = useState<Agent | null>(null);
  const [currentAgent, setCurrentAgent] = useState<Agent | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isLayoutReady, setIsLayoutReady] = useState(false);
  const [isCameraReady, setIsCameraReady] = useState(false);
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [nodes, setNodes, onNodesChange] = useNodesState([]);
  const [edges, setEdges, onEdgesChange] = useEdgesState([]);
  const [expandedNodes, setExpandedNodes] = useState<Set<string>>(
    new Set(["agent-central"])
  ); // Root is expanded by default

  const reactFlowInstance = useRef<any>(null);

  // Function to toggle node expansion
  const toggleNodeExpansion = useCallback((nodeId: string) => {
    setExpandedNodes((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(nodeId)) {
        newSet.delete(nodeId);
      } else {
        newSet.add(nodeId);
      }
      return newSet;
    });
  }, []);

  // Function to close all child trees (keep only root expanded)
  const closeAllChildren = useCallback(() => {
    setExpandedNodes(new Set(["agent-central"]));
  }, []);

  // Function to get child count for a node
  const getChildCount = useCallback((agent: Agent): number => {
    return (agent.actions?.length || 0) + (agent.sub_agents?.length || 0);
  }, []);

  // Advanced layout algorithm
  const forceRepositionAllNodes = useCallback(() => {
    if (!fullAgentTree) return;

    // Track occupied positions to prevent overlaps
    const occupiedPositions: Map<number, Set<number>> = new Map(); // level -> Set of x positions

    // Helper function to calculate the total width needed for a subtree (only considering expanded nodes)
    const calculateSubtreeWidth = (node: TreeNode): number => {
      // If this node is not expanded, it only takes up its own width
      if (!expandedNodes.has(node.id)) {
        return NODE_WIDTH;
      }

      if (node.children.length === 0) {
        return NODE_WIDTH;
      }

      if (node.children.length === 1) {
        return Math.max(NODE_WIDTH, calculateSubtreeWidth(node.children[0]));
      }

      // For multiple children, sum up all their subtree widths plus separations
      let totalWidth = 0;
      node.children.forEach((child, index) => {
        totalWidth += calculateSubtreeWidth(child);
        if (index < node.children.length - 1) {
          totalWidth += MIN_SEPARATION;
        }
      });

      return Math.max(NODE_WIDTH, totalWidth);
    };

    // Helper function to check if a position is occupied
    const isPositionOccupied = (
      x: number,
      level: number,
      buffer: number = NODE_WIDTH / 2
    ): boolean => {
      const levelPositions = occupiedPositions.get(level);
      if (!levelPositions) return false;

      // Check if any occupied position is within the buffer distance
      const positionsArray = Array.from(levelPositions);
      for (const occupiedX of positionsArray) {
        if (Math.abs(x - occupiedX) < buffer) {
          return true;
        }
      }
      return false;
    };

    // Helper function to mark a position as occupied
    const markPositionOccupied = (x: number, level: number): void => {
      if (!occupiedPositions.has(level)) {
        occupiedPositions.set(level, new Set());
      }
      occupiedPositions.get(level)!.add(x);
    };

    // Helper function to find the nearest available position
    const findAvailablePosition = (
      preferredX: number,
      level: number
    ): number => {
      if (!isPositionOccupied(preferredX, level)) {
        return preferredX;
      }

      // Try positions expanding outward from the preferred position
      let offset = NODE_WIDTH + MIN_SEPARATION;
      const maxAttempts = 20; // Prevent infinite loops

      for (let attempt = 0; attempt < maxAttempts; attempt++) {
        // Try to the right
        const rightX = preferredX + offset;
        if (!isPositionOccupied(rightX, level)) {
          return rightX;
        }

        // Try to the left
        const leftX = preferredX - offset;
        if (!isPositionOccupied(leftX, level)) {
          return leftX;
        }

        // Increase offset for next attempt
        offset += NODE_WIDTH + MIN_SEPARATION;
      }

      // Fallback: return preferred position (shouldn't happen with reasonable trees)
      return preferredX;
    };

    // Helper function to check if a group position range is occupied
    const isGroupPositionOccupied = (
      startX: number,
      endX: number,
      level: number
    ): boolean => {
      const levelPositions = occupiedPositions.get(level);
      if (!levelPositions) return false;

      const buffer = NODE_WIDTH / 2;
      const positionsArray = Array.from(levelPositions);

      // Check if any occupied position overlaps with our group range (including buffer)
      for (const occupiedX of positionsArray) {
        if (occupiedX >= startX - buffer && occupiedX <= endX + buffer) {
          return true;
        }
      }
      return false;
    };

    // Helper function to find available space for an entire group of children
    const findAvailableGroupPosition = (
      idealStartX: number,
      groupWidth: number,
      level: number
    ): number => {
      const idealEndX = idealStartX + groupWidth;

      // Check if the ideal position is available
      if (!isGroupPositionOccupied(idealStartX, idealEndX, level)) {
        return idealStartX;
      }

      // Try positions expanding outward from the ideal position
      let offset = NODE_WIDTH + MIN_SEPARATION;
      const maxAttempts = 20;

      for (let attempt = 0; attempt < maxAttempts; attempt++) {
        // Try to the right
        const rightStartX = idealStartX + offset;
        const rightEndX = rightStartX + groupWidth;
        if (!isGroupPositionOccupied(rightStartX, rightEndX, level)) {
          return rightStartX;
        }

        // Try to the left
        const leftStartX = idealStartX - offset;
        const leftEndX = leftStartX + groupWidth;
        if (!isGroupPositionOccupied(leftStartX, leftEndX, level)) {
          return leftStartX;
        }

        // Increase offset for next attempt
        offset += NODE_WIDTH + MIN_SEPARATION;
      }

      // Fallback: return ideal position
      return idealStartX;
    };

    const buildLayoutTree = (
      agent: Agent,
      level: number = 0,
      parentId?: string
    ): TreeNode => {
      const nodeId = level === 0 ? "agent-central" : `agent-${agent.id}`;

      const treeNode: TreeNode = {
        id: nodeId,
        width: NODE_WIDTH,
        height: DEFAULT_NODE_HEIGHT,
        children: [],
        x: 0,
        y: 0,
        level: level,
        parent: parentId,
        hasManualPosition: false,
      };

      // Only include children if this node is expanded
      if (expandedNodes.has(nodeId)) {
        (agent.actions || []).forEach((action) => {
          const actionNode: TreeNode = {
            id: `action-${action.id}-agent-${agent.id}`,
            width: NODE_WIDTH,
            height: DEFAULT_NODE_HEIGHT,
            children: [],
            x: 0,
            y: 0,
            level: level + 1,
            parent: nodeId,
            hasManualPosition: false,
          };
          treeNode.children.push(actionNode);
        });

        (agent.sub_agents || []).forEach((subAgent) => {
          const subAgentNode = buildLayoutTree(subAgent, level + 1, nodeId);
          treeNode.children.push(subAgentNode);
        });
      }

      return treeNode;
    };

    // Recursive function to position a node and expand its children outward
    const positionNodeAndChildren = (
      node: TreeNode,
      nodeX: number,
      levelY: number
    ): void => {
      // Skip manually positioned nodes
      if (node.hasManualPosition) {
        return;
      }

      // Find available position for this node (with collision detection)
      const availableX = findAvailablePosition(nodeX, node.level);

      // Position this node at the available X coordinate
      node.x = availableX;
      node.y = levelY;
      markPositionOccupied(node.x, node.level);

      // Position children if any - expand outward from parent
      if (node.children.length > 0) {
        expandChildrenOutward(node);
      }
    };

    // Expand children outward from their parent's position
    const expandChildrenOutward = (parentNode: TreeNode): void => {
      const children = parentNode.children;
      if (children.length === 0) return;

      const childLevel = parentNode.level + 1;
      const childY =
        parentNode.y + DEFAULT_NODE_HEIGHT + LEVEL_VERTICAL_SPACING;

      if (children.length === 1) {
        // Single child: center it under the parent
        const child = children[0];
        positionNodeAndChildren(child, parentNode.x, childY);
        return;
      }

      // Multiple children: calculate the total width needed for all subtrees
      let totalSubtreeWidth = 0;
      const childSubtreeWidths: number[] = [];

      children.forEach((child, index) => {
        const subtreeWidth = calculateSubtreeWidth(child);
        childSubtreeWidths.push(subtreeWidth);
        totalSubtreeWidth += subtreeWidth;
        if (index < children.length - 1) {
          totalSubtreeWidth += MIN_SEPARATION;
        }
      });

      const idealStartX = parentNode.x - totalSubtreeWidth / 2;

      // Find available space for the entire subtree group
      const groupStartX = findAvailableGroupPosition(
        idealStartX,
        totalSubtreeWidth,
        childLevel
      );

      // Position each child within their allocated subtree space
      let currentX = groupStartX;
      children.forEach((child, index) => {
        const subtreeWidth = childSubtreeWidths[index];
        const childCenterX = currentX + subtreeWidth / 2;
        positionNodeAndChildren(child, childCenterX, childY);
        currentX += subtreeWidth + MIN_SEPARATION;
      });
    };

    const rootNode = buildLayoutTree(fullAgentTree, 0);

    const rootCenterX = 350;
    const rootY = 100;
    positionNodeAndChildren(rootNode, rootCenterX, rootY);

    const positionedNodes: Map<string, { x: number; y: number }> = new Map();

    const collectPositions = (node: TreeNode): void => {
      positionedNodes.set(node.id, { x: node.x, y: node.y });
      node.children.forEach(collectPositions);
    };

    collectPositions(rootNode);

    setNodes((currentNodes: Node[]) =>
      currentNodes.map((node: Node) => {
        const newPosition = positionedNodes.get(node.id);
        if (newPosition) {
          return {
            ...node,
            position: newPosition,
          };
        }
        return node;
      })
    );
  }, [fullAgentTree, setNodes, expandedNodes]);

  // Load capabilities data
  useEffect(() => {
    if (agentId && apiKey) {
      setIsLoading(true);

      AgentService.getAgentDetailed(agentId, setIsLoading, apiKey).subscribe(
        (agent: Agent) => {
          setFullAgentTree(agent);
          setCurrentAgent(agent);
          setIsLoading(false);
        },
        (error: any) => {
          console.error("Error loading agent details:", error);
          setIsLoading(false);
        }
      );
    }
  }, [agentId, apiKey]);

  const handleAddCapability = () => {
    if (onAddCapability) {
      onAddCapability();
    } else {
      setIsModalVisible(true);
    }
  };

  const handleCapabilityAdded = () => {
    // Reload the capabilities
    if (agentId && apiKey) {
      setIsLoading(true);
      AgentService.getAgentDetailed(agentId, setIsLoading, apiKey).subscribe(
        (agent: Agent) => {
          setFullAgentTree(agent);
          setCurrentAgent(agent);
          setIsLoading(false);
        },
        (error: any) => {
          console.error("Error loading agent details:", error);
          setIsLoading(false);
        }
      );
    }
  };

  // Helper function to get nodes for fitView
  const getNodesForFitView = useCallback(
    (allNodes: Node[]) => {
      if (!fullAgentTree) return allNodes;

      const nodesToInclude = allNodes.filter((node) => {
        if (node.id === "agent-central") return true;

        const isDirectAction = (fullAgentTree.actions || []).some(
          (action) =>
            node.id === `action-${action.id}-agent-${fullAgentTree.id}`
        );
        const isDirectSubAgent = (fullAgentTree.sub_agents || []).some(
          (subAgent) => node.id === `agent-${subAgent.id}`
        );

        return isDirectAction || isDirectSubAgent;
      });

      return nodesToInclude;
    },
    [fullAgentTree]
  );

  // Generate nodes and edges
  const generateGraph = useMemo(() => {
    const newNodes: Node[] = [];
    const newEdges: Edge[] = [];

    if (!fullAgentTree) {
      return { nodes: newNodes, edges: newEdges };
    }

    const buildNodesAndEdges = (
      agent: Agent,
      isRoot: boolean = false,
      parentNodeId?: string
    ): void => {
      const agentNodeId = isRoot ? "agent-central" : `agent-${agent.id}`;
      const isExpanded = expandedNodes.has(agentNodeId);

      const agentNode: Node = {
        id: agentNodeId,
        type: "capability",
        position: { x: 0, y: 0 },
        data: {
          label: isRoot ? agentName : agent.agent_name || "Unnamed Agent",
          nodeType: isRoot ? "agent" : "subagent",
          description: agent.description,
          onClick: isRoot
            ? undefined
            : () => {
                // Build parent chain by preserving existing chain and adding current agent ID
                const existingParents = searchParams.get("parents");

                let newParents;
                if (existingParents) {
                  newParents = `${existingParents},${agentId}`;
                } else {
                  newParents = agentId.toString();
                }

                navigate(
                  `/agent/${agent.id}?parents=${encodeURIComponent(newParents)}`
                );
              },
          isDisabled: false,
          childCount: getChildCount(agent),
          isExpanded: isExpanded,
          toggleExpansion: () => toggleNodeExpansion(agentNodeId),
        },
        draggable: false,
      };
      newNodes.push(agentNode);

      if (!isRoot && parentNodeId) {
        newEdges.push({
          id: `edge-${parentNodeId}-${agentNodeId}`,
          source: parentNodeId,
          sourceHandle: "source",
          target: agentNodeId,
          targetHandle: "target",
          style: {
            stroke: "#94a3b8",
            strokeWidth: 2,
          },
        });
      }

      // Only show children if this node is expanded
      if (isExpanded) {
        (agent.actions || []).forEach((action) => {
          const actionNodeId = `action-${action.id}-agent-${agent.id}`;

          const actionNode: Node = {
            id: actionNodeId,
            type: "capability",
            position: { x: 0, y: 0 },
            data: {
              label: action.action_name,
              nodeType:
                action.action_type === "api_call"
                  ? "action-api"
                  : action.action_type === "webhook_creation"
                  ? "action-webhook"
                  : action.action_type === "maitai_workflow"
                  ? "action-workflow"
                  : "action-llm",
              description: action.description,
              isDisabled: false,
              onClick: () =>
                navigate(`/agent/${agent.id}/actions/${action.id}`),
              childCount: 0, // Actions have no children in this graph
              isExpanded: false,
              toggleExpansion: undefined,
            },
            draggable: false,
          };
          newNodes.push(actionNode);

          newEdges.push({
            id: `edge-${agentNodeId}-${actionNodeId}`,
            source: agentNodeId,
            sourceHandle: "source",
            target: actionNodeId,
            targetHandle: "target",
            style: {
              stroke: "#94a3b8",
              strokeWidth: 2,
            },
          });
        });

        (agent.sub_agents || []).forEach((subAgent) => {
          buildNodesAndEdges(subAgent, false, agentNodeId);
        });
      }
    };

    buildNodesAndEdges(fullAgentTree, true, undefined);

    return { nodes: newNodes, edges: newEdges };
  }, [
    fullAgentTree,
    agentName,
    navigate,
    expandedNodes,
    toggleNodeExpansion,
    getChildCount,
    searchParams,
  ]);

  // Update nodes and edges when data changes
  useEffect(() => {
    const { nodes: newNodes, edges: newEdges } = generateGraph;

    // Store timer IDs for cleanup
    let fitViewTimer: NodeJS.Timeout | undefined;
    let cameraReadyTimer: NodeJS.Timeout | undefined;

    // Apply layout to nodes BEFORE setting them to prevent stuttering
    if (!isLoading && newNodes.length > 0 && fullAgentTree) {
      // Calculate positions immediately
      const positionedNodes: Map<string, { x: number; y: number }> = new Map();

      // Simple positioning logic
      const positionTree = (
        agent: Agent,
        x: number,
        y: number,
        level: number = 0
      ): void => {
        const nodeId = level === 0 ? "agent-central" : `agent-${agent.id}`;
        positionedNodes.set(nodeId, { x, y });

        if (expandedNodes.has(nodeId)) {
          const children: { id: string; agent?: Agent }[] = [];

          // Add sorted actions
          const sortedActions = [...(agent.actions || [])].sort((a, b) =>
            (a.action_name || "").localeCompare(b.action_name || "")
          );
          sortedActions.forEach((action) => {
            children.push({ id: `action-${action.id}-agent-${agent.id}` });
          });

          // Add sorted sub-agents
          const sortedSubAgents = [...(agent.sub_agents || [])].sort((a, b) =>
            (a.agent_name || "").localeCompare(b.agent_name || "")
          );
          sortedSubAgents.forEach((subAgent) => {
            children.push({ id: `agent-${subAgent.id}`, agent: subAgent });
          });

          if (children.length > 0) {
            const childY = y + 200;

            if (children.length === 1) {
              const child = children[0];
              if (child.agent) {
                positionTree(child.agent, x, childY, level + 1);
              } else {
                positionedNodes.set(child.id, { x, y: childY });
              }
            } else {
              const spacing = 380;
              const totalWidth = (children.length - 1) * spacing;
              const startX = x - totalWidth / 2;

              children.forEach((child, index) => {
                const childX = startX + index * spacing;
                if (child.agent) {
                  positionTree(child.agent, childX, childY, level + 1);
                } else {
                  positionedNodes.set(child.id, { x: childX, y: childY });
                }
              });
            }
          }
        }
      };

      positionTree(fullAgentTree, 300, 100);

      // Apply positions to nodes before setting them
      const finalNodes = newNodes.map((node) => {
        const newPosition = positionedNodes.get(node.id);
        if (newPosition) {
          return {
            ...node,
            position: newPosition,
          };
        }
        return node;
      });

      setNodes(finalNodes);
      setEdges(newEdges);
      setIsLayoutReady(true); // Layout is ready immediately

      // Camera adjustment happens after layout
      fitViewTimer = setTimeout(() => {
        if (reactFlowInstance.current) {
          reactFlowInstance.current.fitView({
            duration: 0,
            padding: 0.2,
            includeHiddenNodes: false,
            minZoom: 0.1,
            maxZoom: 1.5,
          });
          cameraReadyTimer = setTimeout(() => {
            setIsCameraReady(true); // Only NOW is everything ready
          }, 100);
        }
      }, 100);
    } else {
      setNodes(newNodes);
      setEdges(newEdges);
      setIsLayoutReady(true);
      setIsCameraReady(true); // No camera adjustment needed for empty state
    }

    // Cleanup timers on unmount or re-render
    return () => {
      if (fitViewTimer) clearTimeout(fitViewTimer);
      if (cameraReadyTimer) clearTimeout(cameraReadyTimer);
    };
  }, [
    generateGraph,
    setNodes,
    setEdges,
    isLoading,
    fullAgentTree,
    expandedNodes,
  ]);

  // Expose closeAllChildren function to parent component
  useEffect(() => {
    if (closeAllChildrenRef) {
      closeAllChildrenRef.current = closeAllChildren;
    }
  }, [closeAllChildren, closeAllChildrenRef]);

  // Notify parent about expanded state changes
  useEffect(() => {
    if (onExpandedStateChange) {
      // Check if there are any expanded nodes other than the root
      const hasExpandedChildren = Array.from(expandedNodes).some(
        (nodeId) => nodeId !== "agent-central"
      );
      onExpandedStateChange(hasExpandedChildren);
    }
  }, [expandedNodes, onExpandedStateChange]);

  const onConnect = useCallback(
    (params: Connection) => setEdges((eds) => addEdge(params, eds)),
    [setEdges]
  );

  if (isLoading || !isLayoutReady || !isCameraReady) {
    return (
      <div style={{ width: "100%", height: "500px", position: "relative" }}>
        <div className="absolute inset-0 bg-background z-20 flex items-center justify-center">
          <div className="flex flex-col items-center space-y-4">
            <div className="animate-pulse space-y-4">
              {/* Central node skeleton */}
              <div className="w-80 h-16 bg-muted rounded-lg mx-auto"></div>
              {/* Child nodes skeleton */}
              <div className="flex justify-center space-x-4">
                <div className="w-60 h-12 bg-muted rounded-lg"></div>
                <div className="w-60 h-12 bg-muted rounded-lg"></div>
                <div className="w-60 h-12 bg-muted rounded-lg"></div>
              </div>
              {/* More child nodes */}
              <div className="flex justify-center space-x-6">
                <div className="w-48 h-10 bg-muted rounded-lg"></div>
                <div className="w-48 h-10 bg-muted rounded-lg"></div>
              </div>
            </div>
            <div className="text-sm text-muted-foreground mt-4">
              Loading Tree...
            </div>
          </div>
        </div>
      </div>
    );
  }

  const hasCapabilities =
    (fullAgentTree?.actions || []).length > 0 ||
    (fullAgentTree?.sub_agents || []).length > 0;

  return (
    <div>
      {hasCapabilities ? (
        <div style={{ width: "100%", height: "500px", position: "relative" }}>
          {/* Loading Skeleton */}
          {!isLayoutReady && (
            <div className="absolute inset-0 bg-background z-20 flex items-center justify-center">
              <div className="flex flex-col items-center space-y-4">
                <div className="animate-pulse space-y-4">
                  {/* Central node skeleton */}
                  <div className="w-80 h-16 bg-muted rounded-lg mx-auto"></div>
                  {/* Child nodes skeleton */}
                  <div className="flex justify-center space-x-4">
                    <div className="w-60 h-12 bg-muted rounded-lg"></div>
                    <div className="w-60 h-12 bg-muted rounded-lg"></div>
                    <div className="w-60 h-12 bg-muted rounded-lg"></div>
                  </div>
                  {/* More child nodes */}
                  <div className="flex justify-center space-x-6">
                    <div className="w-48 h-10 bg-muted rounded-lg"></div>
                    <div className="w-48 h-10 bg-muted rounded-lg"></div>
                  </div>
                </div>
                <div className="text-sm text-muted-foreground mt-4">
                  Loading Tree...
                </div>
              </div>
            </div>
          )}

          <div
            style={{
              opacity: isLayoutReady ? 1 : 0,
              transition: "opacity 0.1s",
              width: "100%",
              height: "500px",
            }}>
            <ReactFlow
              nodes={nodes}
              edges={edges}
              onNodesChange={onNodesChange}
              onEdgesChange={onEdgesChange}
              onConnect={onConnect}
              nodeTypes={nodeTypes}
              proOptions={{ hideAttribution: true }}
              nodesDraggable={false}
              nodesConnectable={false}
              elementsSelectable={true}
              minZoom={0.1}
              maxZoom={2}
              defaultEdgeOptions={{
                style: {
                  stroke: "#94a3b8",
                  strokeWidth: 2,
                },
              }}
              onInit={(instance) => {
                reactFlowInstance.current = instance;
              }}>
              <Background gap={16} size={1} color="#f1f5f9" />
              <Controls showInteractive={false} />

              {/* Legend */}
              <div className="absolute bottom-4 left-16 bg-card border border-border rounded-lg p-3 shadow-sm z-10">
                <h4 className="text-sm font-gc-med text-foreground mb-2">
                  Legend
                </h4>
                <div className="space-y-1.5 text-xs">
                  <div className="flex items-center gap-2">
                    <div className="w-3 h-3 rounded border-2 border-[#21b892] bg-card [background-image:linear-gradient(rgba(33,184,146,0.1),rgba(33,184,146,0.1))] dark:[background-image:linear-gradient(rgba(33,184,146,0.2),rgba(33,184,146,0.2))]"></div>
                    <span className="text-foreground">Agent</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <div className="w-3 h-3 rounded border-2 border-[#ffdb6a] bg-card [background-image:linear-gradient(rgba(255,219,106,0.1),rgba(255,219,106,0.1))] dark:[background-image:linear-gradient(rgba(255,219,106,0.2),rgba(255,219,106,0.2))]"></div>
                    <span className="text-foreground">Action</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <div className="w-3 h-3 rounded border-2 border-[#ea5f40] bg-card [background-image:linear-gradient(rgba(234,95,64,0.1),rgba(234,95,64,0.1))] dark:[background-image:linear-gradient(rgba(234,95,64,0.2),rgba(234,95,64,0.2))]"></div>
                    <span className="text-foreground">Sub-Agent</span>
                  </div>
                </div>
              </div>
            </ReactFlow>
          </div>
        </div>
      ) : (
        <div className="flex flex-col items-center justify-center py-24 text-center">
          <div className="space-y-4">
            <BarChart3Icon className="w-12 h-12 text-muted-foreground mx-auto" />
            <div className="space-y-2">
              <h3 className="text-lg font-gc-med text-foreground">
                No Capabilities Yet
              </h3>
              <p className="text-muted-foreground max-w-md mx-auto">
                Add your first capability to see the visual diagram of your
                agent's connections
              </p>
            </div>
            <button
              onClick={handleAddCapability}
              className="text-maitai-green hover:underline text-sm font-gc-med">
              Add First Capability
            </button>
          </div>
        </div>
      )}

      {/* Add Capability Modal - only show if not using external onAddCapability */}
      {!onAddCapability && (
        <AddCapabilityModal
          isOpen={isModalVisible}
          onClose={() => setIsModalVisible(false)}
          agentId={agentId}
          currentAgent={currentAgent}
          onCapabilityAdded={handleCapabilityAdded}
        />
      )}
    </div>
  );
};

export default AgentCapabilitiesGraph;
